import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { MotionRoot } from '@/components/generals/motion-root'
import { PageHero } from '@/components/generals/page-hero'
import { PageIntro } from '@/components/generals/page-intro'
import { InfoGrid } from '@/components/generals/info-grid'
import { NextPage } from '@/components/generals/next-page'
import { ClubhouseSection } from '@/components/landing/clubhouse-section'

import { at, library } from '@/constants/media'

import {
    clubhouseIntroStats,
    clubhouseSpaces,
    clubhouseTimeline,
    nextPageAfter,
    pages
} from '@/constants/pages'

const page = pages.clubhouse
const next = nextPageAfter('clubhouse')

const ClubhousePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title={page.seoTitle} description={page.description} ogImage={page.image.src} />

            <MotionRoot>
                <PageHero
                    index={page.index}
                    eyebrow={page.eyebrow}
                    title={page.title}
                    accent={page.accent}
                    image={page.image}
                    facts={page.facts}
                    next='clubhouse-intro'
                />

                <PageIntro
                    id='clubhouse-intro'
                    index='01'
                    label='The building'
                    aside='Rammed earth · Larch · Glass'
                    statement={<>A building that <em>steps back</em> so the landscape can step forward: low, long and pressed from the earth it stands on.</>}
                    body='Designed with architects who studied the old farmsteads of the Alentejo, the clubhouse borrows their thick earthen walls, deep shade and quiet courtyards, then opens a single wall of glass onto the 18th green.'
                    stats={clubhouseIntroStats}
                    images={{
                        primary: at(library.terraceDining, '72% center'),
                        primaryCaption: 'The Terrace at golden hour'
                    }}
                />

                <ClubhouseSection index='02' images={{ arch: library.holeReturn }} />

                <InfoGrid
                    id='spaces'
                    index='03'
                    label='Inside'
                    heading={<>Six rooms, <em>one long view.</em></>}
                    intro='Every space in the clubhouse faces the course, and every one of them is a little quieter than you expect.'
                    items={clubhouseSpaces}
                    tone='sand'
                />

                <InfoGrid
                    id='story'
                    index='04'
                    label='The story'
                    heading={<>From the first walk <em>to the first tee.</em></>}
                    items={clubhouseTimeline}
                    tone='forest'
                    columns={4}
                />

                <NextPage
                    href={next.href}
                    index={next.index}
                    title={next.title}
                    teaser={next.teaser}
                    image={next.image}
                />
            </MotionRoot>
        </Layout>
    )
}

export default ClubhousePage
